import {
  getProjectMetricsOptions,
  listFacultySuggestionsOptions,
  supportProjectFn,
} from "@/actions/project/handler";
import { authClient } from "@/auth/auth-client";
import { trackProjectSupport } from "@/components/analytics/conversions";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { COMMON_FACULTIES, normalizeFaculty } from "@/utils/faculty";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Check,
  ChevronsUpDown,
  Heart,
  Loader2,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface SupportSystemProps {
  projectId: string;
  projectTitle: string;
  user: unknown;
}

export function SupportSystem({
  projectId,
  projectTitle,
  user,
}: SupportSystemProps) {
  const [isSupportDialogOpen, setIsSupportDialogOpen] = useState(false);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [faculty, setFaculty] = useState("");
  const [openCombobox, setOpenCombobox] = useState(false);
  const [showAllFaculties, setShowAllFaculties] = useState(false);

  const queryClient = useQueryClient();
  const { data: metricsData } = useQuery(getProjectMetricsOptions(projectId));
  const { data: suggestionsData } = useQuery(listFacultySuggestionsOptions());

  const dbSuggestions = suggestionsData?.body?.suggestions || [];
  // Merge common faculties with DB suggestions and remove duplicates
  const suggestions = Array.from(
    new Set([...COMMON_FACULTIES, ...dbSuggestions]),
  ).sort();

  const metricsRaw = metricsData?.body;
  const metrics = metricsRaw && "total" in metricsRaw ? metricsRaw : null;

  const supportMutation = useMutation({
    mutationFn: (data: { projectId: string; faculty: string }) =>
      supportProjectFn({ data }),
    onSuccess: (res) => {
      if (res.status === 201) {
        trackProjectSupport(projectId, projectTitle);
        toast.success("Desteğiniz kaydedildi, teşekkürler!");
        setIsSupportDialogOpen(false);
        setFaculty("");
        queryClient.invalidateQueries({
          queryKey: ["projectMetrics", projectId],
        });
      } else {
        toast.error(res.body?.message || "Bir hata oluştu.");
      }
    },
    onError: () => toast.error("İşlem başarısız oldu."),
  });

  const handleSupportClick = () => {
    if (!user) {
      setIsAuthDialogOpen(true);
      return;
    }
    setIsSupportDialogOpen(true);
  };

  const handleSupportSubmit = () => {
    const normalized = normalizeFaculty(faculty);
    if (!normalized) {
      toast.error("Lütfen geçerli bir fakülte bilginizi girin.");
      return;
    }
    supportMutation.mutate({ projectId, faculty: normalized });
  };

  return (
    <div className="mt-8 space-y-6">
      {/* Metrics Summary */}
      {metrics && metrics.total > 0 && (
        <div className="rounded-xl border bg-secondary/5 p-4 sm:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <span className="font-bold text-lg">
              {metrics.total} Toplam Destek
            </span>
          </div>

          <div className="space-y-3">
            <p className="font-medium text-muted-foreground text-sm uppercase tracking-wider">
              En Çok Destek Veren Fakülteler
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {(showAllFaculties
                ? metrics.facultyBreakdown
                : metrics.facultyBreakdown.slice(0, 5)
              ).map((item) => (
                <div
                  key={item.faculty}
                  className="flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-sm shadow-xs"
                >
                  <span className="font-semibold text-primary">
                    {item.count}
                  </span>
                  <span className="text-muted-foreground">{item.faculty}</span>
                </div>
              ))}
              {metrics.facultyBreakdown.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllFaculties(!showAllFaculties)}
                  className="rounded-full px-2.5 py-1.5 font-semibold text-primary text-xs transition-colors hover:bg-secondary/10 hover:underline"
                >
                  {showAllFaculties
                    ? "Daha az göster"
                    : `+${metrics.facultyBreakdown.length - 5} daha fazla`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Support Button */}
      <Button
        onClick={handleSupportClick}
        size="lg"
        className="group relative w-full overflow-hidden font-bold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] sm:w-auto"
      >
        <span className="relative z-10 flex items-center gap-2">
          {supportMutation.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Heart
              className={cn(
                "h-5 w-5",
                metrics && metrics.total > 0 ? "fill-white" : "",
              )}
            />
          )}
          Projeye Destek Ver
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </Button>

      {/* Support Dialog */}
      <Dialog open={isSupportDialogOpen} onOpenChange={setIsSupportDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{projectTitle}</DialogTitle>
            <DialogDescription>
              Projeye destek vermek için fakülte bilginizi girin. Bu bilgi
              kampüs içindeki talebi göstermek için kullanılacaktır.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label className="font-medium text-sm">Fakülteniz</label>
              <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {faculty || "Fakülte seçin veya yazın..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Fakülte ara..."
                      onValueChange={(val) => setFaculty(val)}
                    />
                    <CommandList>
                      <CommandEmpty>
                        <button
                          type="button"
                          className="flex h-full w-full items-center px-4 py-2 text-sm hover:bg-accent"
                          onClick={() => setOpenCombobox(false)}
                        >
                          "{faculty}" olarak ekle (Enter'a basın)
                        </button>
                      </CommandEmpty>
                      <CommandGroup>
                        {suggestions.map((s: string) => (
                          <CommandItem
                            key={s}
                            value={s}
                            onSelect={(currentValue) => {
                              setFaculty(currentValue);
                              setOpenCombobox(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                faculty === s ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {s}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              onClick={handleSupportSubmit}
              disabled={supportMutation.isPending}
              className="w-full"
            >
              {supportMutation.isPending ? "Kaydediliyor..." : "Desteği Onayla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auth Dialog */}
      <Dialog open={isAuthDialogOpen} onOpenChange={setIsAuthDialogOpen}>
        <DialogContent className="p-0 sm:max-w-[400px]">
          {/* We hide the default LoginForm layout stuff and just use its tabs */}
          <div className="p-6">
            <DialogHeader className="mb-4">
              <DialogTitle className="text-center text-xl">
                Giriş Yapmanız Gerekiyor
              </DialogTitle>
              <DialogDescription className="text-center">
                Destek vermek ve ATC ekosistemine katılmak için lütfen giriş
                yapın.
              </DialogDescription>
            </DialogHeader>
            <LoginFormInModal />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
import { Separator } from "@/components/ui/separator";

function LoginFormInModal() {
  // We can't easily rip out just the parts of LoginForm without duplication
  // since it's a large file. But we can import the sub-components if they were exported.
  // Since they aren't, I will use a simplified version here or refactor LoginForm later.
  // For now, I'll leverage social login as it's the fastest way for campus users.

  return (
    <div className="flex flex-col gap-4">
      <Button
        variant="outline"
        className="h-12 w-full gap-2 border-2 font-semibold"
        onClick={() =>
          authClient.signIn.social({
            provider: "google",
            callbackURL: window.location.href,
          })
        }
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        Google ile Hızlı Giriş
      </Button>

      <div className="relative my-2">
        <div className="absolute inset-0 flex items-center">
          <Separator />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Veya Login Sayfasına Git
          </span>
        </div>
      </div>

      <Button
        variant="ghost"
        className="w-full"
        onClick={() => (window.location.href = "/password/login")}
      >
        Tüm Giriş Seçeneklerini Gör
      </Button>
    </div>
  );
}
